import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

function PmsCancellationPolicies({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [policies, setPolicies] = useState([]);
  const [form, setForm] = useState({
    policy_name: '',
    description: '',
    window_hours: 24,
    penalty_type: 'FLAT_AMOUNT',
    penalty_value: 0,
    is_active: true
  });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  // Mapping panel state
  const [mappingPolicy, setMappingPolicy] = useState(null);
  const [ratePlans, setRatePlans] = useState([]);
  const [mappedIds, setMappedIds] = useState(new Set());
  const [mappingBusy, setMappingBusy] = useState(false);
  const [mappingMsg, setMappingMsg] = useState('');

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadPolicies = async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/pms-policies/', { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setPolicies(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.id]);

  const save = async () => {
    const cleanName = String(form.policy_name || '').trim();
    if (!cleanName) {
      setError('Policy Name is required and cannot be blank.');
      return;
    }
    setBusy(true); setError(''); setMsg('');
    try {
      const url = editingId ? `/api/v1/pms-policies/${editingId}` : '/api/v1/pms-policies/';
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: headers(),
        body: JSON.stringify({ ...form, policy_name: cleanName })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');

      setMsg('Policy saved successfully');
      setForm({ policy_name: '', description: '', window_hours: 24, penalty_type: 'FLAT_AMOUNT', penalty_value: 0, is_active: true });
      setEditingId(null);
      loadPolicies();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const edit = (p) => {
    setForm({
      policy_name: p.policy_name,
      description: p.description || '',
      window_hours: Number(p.window_hours),
      penalty_type: p.penalty_type,
      penalty_value: Number(p.penalty_value),
      is_active: p.is_active
    });
    setEditingId(p.policy_id);
    setError('');
    setMsg('');
    setMappingPolicy(null);
  };

  const del = async (id) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    try {
      await fetch(`/api/v1/pms-policies/${id}`, { method: 'DELETE', headers: headers() });
      loadPolicies();
    } catch (err) {
      console.error(err);
    }
  };

  const cancelEdit = () => {
    setForm({ policy_name: '', description: '', window_hours: 24, penalty_type: 'FLAT_AMOUNT', penalty_value: 0, is_active: true });
    setEditingId(null);
    setError(''); setMsg('');
  };

  // --- Mapping ---

  const openMapping = async (p) => {
    setMappingPolicy(p);
    setEditingId(null);
    setMappingMsg('');
    setRatePlans([]);
    setMappedIds(new Set());
    try {
      const res = await fetch(`/api/v1/pms-policies/${p.policy_id}/rate-plans`, { headers: headers() });
      if (!res.ok) throw new Error('Failed to load rate plans');
      const data = await res.json();
      setRatePlans(data);
      setMappedIds(new Set(data.filter(r => r.is_mapped).map(r => r.id)));
    } catch (err) {
      setMappingMsg('Error: ' + err.message);
    }
  };

  const toggleRatePlan = (id) => {
    setMappedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const saveMapping = async () => {
    setMappingBusy(true); setMappingMsg('');
    try {
      const res = await fetch(`/api/v1/pms-policies/${mappingPolicy.policy_id}/rate-plans`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ rate_plan_ids: Array.from(mappedIds) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save mapping');
      setMappingMsg(`Mapping saved — ${data.mapped_count} rate plan(s) assigned.`);
    } catch (err) {
      setMappingMsg('Error: ' + err.message);
    } finally {
      setMappingBusy(false);
    }
  };

  return (
    <div className="page-shell" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
      <h3 style={{ color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '0.5rem' }}>Cancellation Policies Master</h3>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 300px', gap: '2rem' }}>

        {/* List Section */}
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ background: '#34495e', color: '#fff' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Policy Name</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Window (Hrs)</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '0.75rem', textAlign: 'right' }}>Penalty</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {policies.map(p => (
                <tr key={p.policy_id} style={{ borderBottom: '1px solid #bdc3c7', background: mappingPolicy?.policy_id === p.policy_id ? '#eaf4ff' : 'transparent' }}>
                  <td style={{ padding: '0.75rem' }}>
                    <div style={{fontWeight: 600}}>{p.policy_name}</div>
                    <div style={{fontSize: '0.8rem', color: '#7f8c8d'}}>{p.description}</div>
                  </td>
                  <td style={{ padding: '0.75rem' }}>{p.window_hours} hr</td>
                  <td style={{ padding: '0.75rem' }}>{p.penalty_type}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>{Number(p.penalty_value).toLocaleString()}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <button onClick={() => edit(p)} style={{ marginRight: '0.5rem', background: 'none', border: 'none', color: '#3498db', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => openMapping(p)} style={{ marginRight: '0.5rem', background: 'none', border: 'none', color: '#27ae60', cursor: 'pointer', fontWeight: 600 }}>Map</button>
                    <button onClick={() => del(p.policy_id)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer' }}>Del</button>
                  </td>
                </tr>
              ))}
              {policies.length === 0 && <tr><td colSpan={5} style={{ padding: '1rem', textAlign: 'center', color: '#7f8c8d' }}>No policies found. Create one.</td></tr>}
            </tbody>
          </table>

          {/* Mapping Panel */}
          {mappingPolicy && (
            <div style={{ marginTop: '1.5rem', border: '1px solid #3498db', borderRadius: '6px', padding: '1rem', background: '#f0f8ff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, color: '#2c3e50' }}>
                  Map Rate Plans → <span style={{ color: '#2980b9' }}>{mappingPolicy.policy_name}</span>
                </h4>
                <button onClick={() => setMappingPolicy(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#7f8c8d' }}>✕</button>
              </div>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#555' }}>
                Check the rate plans that should use this cancellation policy.
              </p>
              {ratePlans.length === 0 ? (
                <p style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>No active rate plans found for this property.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem', marginBottom: '1rem' }}>
                  {ratePlans.map(rp => (
                    <label key={rp.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: mappedIds.has(rp.id) ? '#d4edda' : '#fff', border: '1px solid ' + (mappedIds.has(rp.id) ? '#28a745' : '#cbd5e0'), borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input
                        type="checkbox"
                        checked={mappedIds.has(rp.id)}
                        onChange={() => toggleRatePlan(rp.id)}
                      />
                      <span>
                        <span style={{ fontWeight: 600 }}>{rp.rate_plan_code}</span>
                        <span style={{ color: '#555', marginLeft: '0.3rem' }}>— {rp.rate_plan_name}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {mappingMsg && (
                <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '4px', background: mappingMsg.startsWith('Error') ? '#f8d7da' : '#d4edda', color: mappingMsg.startsWith('Error') ? '#721c24' : '#155724', fontSize: '0.875rem' }}>
                  {mappingMsg}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={saveMapping}
                  disabled={mappingBusy || ratePlans.length === 0}
                  style={{ padding: '0.55rem 1.2rem', background: '#27ae60', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, opacity: mappingBusy ? 0.7 : 1 }}
                >
                  {mappingBusy ? 'Saving...' : 'Save Mapping'}
                </button>
                <button
                  onClick={() => setMappingPolicy(null)}
                  style={{ padding: '0.55rem 1rem', background: '#fff', border: '1px solid #cbd5e0', borderRadius: '4px', cursor: 'pointer', color: '#2c3e50' }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Form Section */}
        <div className="card card-body" style={{ height: 'fit-content' }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>{editingId ? 'Edit Policy' : 'New Policy'}</h4>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>Policy Name</label>
            <input
              value={form.policy_name}
              onChange={e => setForm({...form, policy_name: e.target.value})}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
              placeholder="e.g. 24h Free Cancellation"
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              rows={2}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>Window (Hours)</label>
              <input
                type="number"
                value={form.window_hours}
                onChange={e => setForm({...form, window_hours: e.target.value})}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                title="Hours before check-in allowing free cancellation"
              />
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
             <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>Penalty Type</label>
             <select
               value={form.penalty_type}
               onChange={e => setForm({...form, penalty_type: e.target.value})}
               style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
             >
                <option value="FLAT_AMOUNT">Flat Amount (Currency)</option>
                <option value="PERCENT_TOTAL">% of Total Booking</option>
                <option value="FIRST_NIGHT">First Night Charge</option>
                <option value="FULL_RETENTION">Full Retention (100%)</option>
             </select>
          </div>

          {form.penalty_type !== 'FULL_RETENTION' && form.penalty_type !== 'FIRST_NIGHT' && (
             <div style={{ marginBottom: '1rem' }}>
               <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>Penalty Value</label>
               <input
                 type="number"
                 value={form.penalty_value}
                 onChange={e => setForm({...form, penalty_value: e.target.value})}
                 style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                 placeholder={form.penalty_type === 'PERCENT_TOTAL' ? 'e.g. 50' : 'e.g. 500'}
               />
             </div>
          )}

          {error && <div className="error-banner">{error}</div>}
          {msg && <div className="success-banner">{msg}</div>}

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {editingId && <button onClick={cancelEdit} style={{ flex: 1, padding: '0.6rem', border: '1px solid #cbd5e0', background: '#fff', color: '#2c3e50', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>}
            <button onClick={save} disabled={busy} className="btn-primary" style={{ flex: 1, padding: '0.6rem', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', opacity: busy?0.7:1 }}>
               {busy ? 'Saving...' : (editingId ? 'Update' : 'Create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PmsCancellationPolicies;
